import { Interpolation, Theme } from '@emotion/react';
import { ButtonProps, reactNodeTextContent, Text } from '@lbcamden/lib-react';
import { FunctionComponent, ReactNode } from 'react';

import { defaultFallbackValue } from '~/common/config/constants';

import { SensitiveValue } from '../SensitiveValue';
import {
  DetailsSection,
  DetailsHeader,
  ItemDetailsRoot,
  ActionsContainer,
  ItemActionButton,
  InfoGrid,
  TitleLabel,
  TitleSection,
} from './ItemDetails.styled';

export type ItemDetailsProps = {
  css?: Interpolation<Theme>;
  /** @default 'Title' */
  titleLabel?: string;
  title: string;
  /** Specify a fallback for info items with an empty value */
  detailFallbackValue?: ReactNode;
  actions?: ItemAction[];
  /** Default section with "Information" header */
  information?: InfoItem[];
  /** Custom sections with user-defined headers */
  sections?: ItemDetailsSection[];
};

export type ItemDetailsSection = {
  title: string;
  details?: InfoItem[];
  content?: ReactNode;
  actions?: ItemAction[];
};

export type ItemAction = ButtonProps & { key: string };

export type InfoItem =
  | [ReactNode, ReactNode]
  | {
      label: ReactNode;
      value: ReactNode;
      sensitive?: boolean;
    };

const transformInfoItem = (item: InfoItem): [ReactNode, ReactNode] => {
  if (Array.isArray(item)) return item;

  return [
    item.label,
    item.sensitive ? (
      // oxlint-disable-next-line react/jsx-key -- prop tuple, not rendered children
      <SensitiveValue label={reactNodeTextContent(item.label)}>{item.value}</SensitiveValue>
    ) : (
      item.value
    ),
  ];
};

const renderSection = (
  { title, details, content, actions }: ItemDetailsSection,
  detailFallbackValue: ReactNode = '',
) => (
  <DetailsSection data-testid={`item-details-section - ${title}`} key={title}>
    <DetailsHeader>{title}</DetailsHeader>
    {details && (
      <InfoGrid>
        {details
          .map(transformInfoItem)
          .map(([field, value], index) => [
            <dt key={index}>{field}:</dt>,
            <dd key={`${index}-value`}>
              {['', undefined, null].includes(value as never) ? detailFallbackValue : value}
            </dd>,
          ])}
      </InfoGrid>
    )}
    {content}
    {!!actions?.length && (
      <ActionsContainer>
        {actions.map(({ key, ...actionProps }) => (
          <ItemActionButton key={key} {...actionProps} />
        ))}
      </ActionsContainer>
    )}
  </DetailsSection>
);

export const ItemDetails: FunctionComponent<ItemDetailsProps> = ({
  titleLabel = 'Title',
  title,
  detailFallbackValue = defaultFallbackValue,
  actions,
  information,
  sections,
  ...props
}) => (
  <ItemDetailsRoot data-testid={`item-details - ${title}`} {...props}>
    <TitleSection>
      <TitleLabel>{titleLabel}</TitleLabel>
      <Text variant="h1">{title}</Text>
    </TitleSection>

    {!!actions?.length && (
      <DetailsSection>
        <DetailsHeader>Actions</DetailsHeader>
        <ActionsContainer>
          {actions.map(({ key, ...actionProps }) => (
            <ItemActionButton key={key} {...actionProps} />
          ))}
        </ActionsContainer>
      </DetailsSection>
    )}

    {!!information?.length &&
      renderSection({ title: 'Information', details: information }, detailFallbackValue)}

    {sections?.map((section) => renderSection(section, detailFallbackValue))}
  </ItemDetailsRoot>
);
